import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Assignment as AssignmentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { PageLayout, PaginationBar } from '../../../shared/components';
import { useMe } from '../../../shared/hooks/useMe';
import { canWriteContract } from '../../../shared/permissions/capabilities';
import { colors, typography } from '../../../shared/styles/theme';
import { getSessions } from '../utils/contractApi';
import type { SessionListItem } from '../types/contracts';
import ContractReviewSubmissionList from './ContractReviewSubmissionList';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

const tealButtonSx = {
  backgroundColor: colors.primaryLight,
  color: colors.text.light,
  textTransform: 'none' as const,
  fontWeight: typography.fontWeight.medium,
  borderRadius: 2,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: colors.sidebar.selected,
    boxShadow: 'none',
  },
} as const;

const ContractReviewListPage: React.FC = () => {
  const navigate = useNavigate();
  const { me } = useMe();
  const canUploadContract = canWriteContract(me);
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientNameSearch, setClientNameSearch] = useState('');
  const [clientNameQuery, setClientNameQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    const searchTerm = clientNameQuery.trim();
    try {
      const res = await getSessions({
        page,
        page_size: pageSize,
        client_name: searchTerm.length >= 3 ? searchTerm : undefined,
      });
      setSessions(res.sessions);
      setTotalItems(res.total_items);
    } catch (e) {
      setError('Failed to load reviews.');
      setSessions([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, clientNameQuery]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    const t = setTimeout(() => {
      setClientNameQuery(clientNameSearch);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [clientNameSearch]);

  const openReview = (sessionUuid: string) => {
    navigate(`/contract-review/${sessionUuid}`);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = totalItems === 0 ? 0 : Math.min(page * pageSize, totalItems);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <PageLayout>
      <Box>
        <Box
          sx={{
            pb: 3,
            mb: 3,
            borderBottom: `1px solid ${colors.border.light}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.bold,
              fontSize: { xs: typography.fontSize['2xl'], sm: typography.fontSize['4xl'] },
              color: '#111827',
              lineHeight: typography.lineHeight.tight,
            }}
          >
            Submission History
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              color: colors.text.secondary,
              fontSize: typography.fontSize.md,
            }}
          >
            View and manage all contract submissions
          </Typography>
        </Box>

        {/* Search card */} 
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.border.light}`,
            backgroundColor: colors.background.card,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by client"
            value={clientNameSearch}
            onChange={(e) => setClientNameSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.secondary, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: colors.background.card,
              },
            }}
          />
        </Paper>

        {/* Results count */} 
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ color: colors.text.secondary, fontSize: typography.fontSize.md }}>
            {totalItems === 0 ? 'Showing 0 submissions' : `Showing ${startIndex}-${endIndex} of ${totalItems} submissions`}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : totalItems === 0 && clientNameQuery.trim() === '' ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: colors.background.card,
              border: `1px solid ${colors.border.light}`,
              borderRadius: 2,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 48, color: colors.text.disabled, mb: 1.5 }} />
            <Typography variant="body1" sx={{ color: colors.text.secondary, mb: 2 }}>
              No reviews yet. {canUploadContract ? 'Upload a contract to get started.' : 'You do not have permission to upload contracts.'}
            </Typography>
            {canUploadContract && (
              <Button
                variant="contained"
                onClick={() => navigate('/contract-review/upload')}
                sx={{ ...tealButtonSx, minHeight: 40, px: 2.5 }}
              >
                Upload contract
              </Button>
            )}
          </Paper>
        ) : sessions.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: colors.background.card,
              border: `1px solid ${colors.border.light}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              No reviews match &quot;{clientNameQuery.trim()}&quot;. Try a different client name.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ mb: 3 }}>
            <ContractReviewSubmissionList sessions={sessions} onOpen={openReview} />
          </Box>
        )}

        {/* Pagination (bottom card) */} 
        {totalItems > 0 && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
            onRowsPerPageChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </Box>
    </PageLayout>
  );
};

export default ContractReviewListPage;
